import { Formik, Form } from "formik";
import React, { useEffect, useState } from "react";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import {
  handleError,
  hasAnyModification,
  sweetAlertError,
} from "../../utils/AppFunctions";
import Loader from "../../core-component/ui/Loader";
import { IMAGE_TYPE } from "../../utils/AppEnumerations";
import { ICFileUpload } from "../../core-component/form";
import { fetchUploadImageService } from "../global/image-service/ImageServices";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";
import {
  handleSubmitGalleryDirectoryItems,
  IGalleryDirectoryItem,
} from "./GalleryDirectoryItemController";

interface IGalleryDirectoryFormProps {
  handleClose: (status: boolean) => void;
  galleryDiretoryId: string;
}

const GalleryDirectoryFormItem: React.FC<IGalleryDirectoryFormProps> = ({
  handleClose,
  galleryDiretoryId,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folderImage, setFolderImage] = useState<string[]>([]);
  const [
    galleryDirectoryFormInitialValues,
    setGalleryDirectoryFormInitialValues,
  ] = useState<IGalleryDirectoryItem>();

  useEffect(() => {
    setLoading(true);
    setGalleryDirectoryFormInitialValues({
      gallery_directory_id: galleryDiretoryId,
      gallery_media: [
        {
          media_url: "",
        },
      ],
    });
    setLoading(false);
  }, []);

  const checkFormModification = (values: IGalleryDirectoryItem) => {
    if (
      galleryDirectoryFormInitialValues &&
      hasAnyModification(values, galleryDirectoryFormInitialValues)
    ) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = async (values: IGalleryDirectoryItem) => {
    try {
      setSubmitLoading(true);
      const updatedTypeMedia = folderImage
        ? folderImage.map((url) => ({
            media_url: url,
          }))
        : [];

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append(`ImageFile`, file);
        formData.append(`type`, IMAGE_TYPE.GalleryItem);
        values.gallery_directory_id &&
          formData.append(`typeId`, values.gallery_directory_id.toString());

        const mediaUrl = await fetchUploadImageService(formData);
        if (!mediaUrl) {
          return null;
        }

        const newMediaItem = {
          media_url: mediaUrl,
        };
        updatedTypeMedia.push(newMediaItem);
      }

      await handleSubmitGalleryDirectoryItems(
        {
          ...values,
          gallery_media: updatedTypeMedia,
        },
        setSubmitLoading,
        handleClose
      );
    } catch (e: any) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
      handleError(e, "galleryDirectoryItemsSubmit");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {galleryDirectoryFormInitialValues && (
            <Formik
              initialValues={galleryDirectoryFormInitialValues}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              {(formikPorps) => {
                const { handleSubmit, values } = formikPorps;

                return (
                  <FormRightModalWrapper
                    containerClassName="!w-1/4"
                    onClose={() => checkFormModification(values)}
                    title={t("gallery_directory_item")}
                  >
                    <Form
                      onSubmit={handleSubmit}
                      className="flex flex-col h-full "
                    >
                      <div className="flex-1 overflow-y-scroll pt-2.5 mb-4 space-y-4">
                        <div className="mt-4">
                          <ICFileUpload
                            title={t("upload_image")}
                            maxFiles={10}
                            maxFileSizeMB={10}
                            acceptedFileTypes={["jpg", "png", "jpeg", "mp4"]}
                            onFilesSelected={setSelectedFiles}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            data={folderImage}
                            setData={setFolderImage}
                          />
                        </div>
                      </div>

                      <ICButton
                        loading={submitLoading}
                        className={`${
                          selectedFiles.length === 0 ? "disable-button" : ""
                        } w-full`}
                        children={t("submit")}
                        disabled={selectedFiles.length === 0}
                      />
                    </Form>
                  </FormRightModalWrapper>
                );
              }}
            </Formik>
          )}

          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default GalleryDirectoryFormItem;
